import { useEffect } from 'react';
import { AT } from '../../../actions/actionTypes';
import { Transfer } from './model';
import { TransferDefault } from './defaults';
import { TransferDialogs, TransferStatus } from './consts';
import {
  msgCheckingCodeError,
  msgGettingCodeError,
  msgTransferingError,
  msgValidatingError,
} from './messages';
import Logger from '../../../libs/logger';  import { LogArea } from '../../../libs/logger/const';
import * as MyKeysApiTransfer from '../../../core/apis/mykeys/transfer';
import { MyKeysPromiseMap } from '../../../core/apis/mykeys/mappings';
import { ErrorDataResponse } from '../../../interfaces';
import useTx from './useTx';
import useDispatcher from '../../../hooks/useDispatcher';
import useCallStatus from '../../../hooks/useCallStatus';
import useUserInfo, {
  UseUserInfoDataDefaultByUser,
} from '../../../hooks/useUserInfo';
import { PKey } from '../../devices';

export const useTxSend = () => {
  const {
    refreshData,
    closePanel,
    changeScreen,
    showTxDevices: showDevices,
    send: { transfer: tx },
  } = useTx();
  const { doAction } = useDispatcher();

  const receiver = useUserInfo(UseUserInfoDataDefaultByUser(tx.receiver));

  const validateCall = useCallStatus();
  const requestCodeCall = useCallStatus();
  const checkCodeCall = useCallStatus();
  const sendTxDevicesCall = useCallStatus();

  const updateTx = (newTx: Transfer) => {
    doAction<Transfer>(AT.DIALOG_TRANSFER_SEND_UPDATE_TRANSFER, newTx);
  };

  const updateStatus = (status: TransferStatus) => {
    updateTx({ ...tx, status });
  };

  useEffect(() => {
    updateTx({ ...tx, receiver: receiver.userInfo });
  }, [receiver.userInfo]);

  const reset = () => {
    updateTx({...TransferDefault, id: undefined});
    receiver.reset();
    validateCall.reset();
    requestCodeCall.reset();
    checkCodeCall.reset();
    sendTxDevicesCall.reset();
  };

  const setTxDevices = (devices: PKey[]) => {
    doAction<PKey[]>(AT.DIALOG_TRANSFER_SEND_UPDATE_DEVICES, devices)
  };

  const validate = () => {
    Logger.info(['validateSendTransfer'], LogArea.Redux);
    MyKeysApiTransfer.sendValidateTransfer(tx)
      .then(MyKeysPromiseMap)
      .then((data: number) => {
        validateCall.success();
        updateTx({
          ...tx,
          id: data,
          status: TransferStatus.VALIDATED,
        });
      })
      .catch((err: ErrorDataResponse) => {
        let errMsg = msgValidatingError;
        validateCall.error(errMsg);
      });
    validateCall.fetch();
  };

  const getCode = () => {
    Logger.info(['getCode'], LogArea.Redux);
    MyKeysApiTransfer.getUserCode(tx)
      .then(MyKeysPromiseMap)
      .then((data: boolean) => {
        if (!data) throw Error('Request Code Failed');
        requestCodeCall.success();
        updateStatus(TransferStatus.CODE_REQUESTED);
      })
      .catch((err: ErrorDataResponse) => {
        let errMsg = msgGettingCodeError;
        requestCodeCall.error(errMsg);
      });
    requestCodeCall.fetch();
  };

  const checkCode = (code: string) => {
    Logger.info(['checkCode'], LogArea.Redux);
    MyKeysApiTransfer.checkUserCode(tx, code)
      .then(MyKeysPromiseMap)
      .then((data: boolean) => {
        if (!data) throw Error('Request Code Failed');
        checkCodeCall.success();
        updateStatus(TransferStatus.CONFIRMED);
        changeScreen(TransferDialogs.SEND_CONFIRM);
      })
      .catch((err: ErrorDataResponse) => {
        let errMsg = msgCheckingCodeError;
        checkCodeCall.error(errMsg);
      });
    checkCodeCall.fetch();
  };

  const sendTxDevices = () => {
    Logger.info(['transferDevices'], LogArea.Redux);
    MyKeysApiTransfer.sendTransferDevices(tx)
      .then(MyKeysPromiseMap)
      .then((data: boolean) => {
        sendTxDevicesCall.success();
        updateStatus(TransferStatus.COMPLETED);
        refreshData();
      })
      .catch((err: ErrorDataResponse) => {
        let errMsg = msgTransferingError;
        sendTxDevicesCall.error(errMsg);
      });
    sendTxDevicesCall.fetch();
  };

  const showTxDevices = () => showDevices(tx.id, tx.devices);

  return {
    tx,
    useReceiver: receiver,
    reset,
    showTxDevices,
    setTxDevices,
    setStatus: updateStatus,
    validate,
    validateStatus: validateCall.status,
    getCode,
    getCodeStatus: requestCodeCall.status,
    checkCode,
    checkCodeStatus: checkCodeCall.status,
    sendTxDevices,
    sendTxDevicesStatus: sendTxDevicesCall.status,
    closePanel,
    changeScreen
  };
};

export default useTxSend;
